import type { ChatMessages } from "@storytell/cp-client";
import { SolidStreamingMarkdown } from "@storytell/markdown";
import { RichTextEditor } from "@storytell/rich-text";
import type { StreamingBlock } from "@storytell/streaming";
import { AnimateStaggeredChildren } from "@storytell/ui/animation";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCheck, TbCopy } from "solid-icons/tb";
import { Show, createMemo, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { LogoNoText } from "~/components/brand";
import { UnifiedCitationRenderer } from "~/components/chat/UnifiedCitationRenderer";
import { CopyButton, useCopy } from "~/components/ui";
import type { WebSearchResultsCollection } from "~/utils/citationResolver";
import { buildWebSearchCollection } from "~/utils/webSearchCollectionBuilder";
import { useWire } from "~/wire";
import { TextSelectionHandler } from "../TextSelectionHandler";
import { createMarkdownStreamerFromDeltas } from "./internal/markdownStreamerFromDeltas";

interface UserFacingMessageProps {
  block: StreamingBlock;
}

export const UserFacingMessage = (props: UserFacingMessageProps) => {
  const wire = useWire();
  const operation = () => wire.stream.operation.get(props.block.operationId);

  // Build web search results collection from all webSearchResults objects in the same message
  const webSearchResults = createMemo<WebSearchResultsCollection | undefined>(() => {
    const op = operation();
    if (!op) return undefined;

    const messageId = op.messageId;
    if (!messageId) return undefined;

    const message = wire.stream.message.get(messageId);
    if (!message) return undefined;

    const allWebSearchResults: ChatMessages.WebSearchResult[] = [];

    // Look through all operations in this message
    for (const operationId of message.operations ?? []) {
      const messageOp = wire.stream.operation.get(operationId);
      if (!messageOp) continue;

      // Look through all blocks in this operation
      for (const blockId of messageOp.blocks ?? []) {
        const block = wire.stream.block.get(blockId);
        if (block?.webSearchResults) {
          // Collect individual webSearchResults objects from each block
          allWebSearchResults.push(block.webSearchResults);
        }
      }
    }

    // Build collection for efficient lookup by tool ID
    return allWebSearchResults.length > 0 ? buildWebSearchCollection(allWebSearchResults) : undefined;
  });

  const hideContentOnComplete = () => {
    const op = operation();
    if (!op) return false;
    const activity = op.activity;
    if (activity === "user_kb_search_v1" && op.isComplete) {
      return true;
    }
    return false;
  };

  const hasContent = () => !!props.block.deltas.length && !hideContentOnComplete();

  const getText = () => props.block.deltas.map((delta) => delta.text).join("");
  const [hovered, setHovered] = createSignal(false);
  const { copied, copy } = useCopy({ timeout: 1000 });
  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    await copy(getText());
  };

  // Create streaming markdown instance
  const markdownStreamer = createMarkdownStreamerFromDeltas(() => props.block.deltas.map((delta) => delta.text));

  // Ref for text selection
  const [contentRef, setContentRef] = createSignal<HTMLDivElement>();

  return (
    <Show when={props.block} fallback={<div class="text-sm text-gray-700 dark:text-gray-300">Loading...</div>}>
      <Show when={hasContent()}>
        <>
          <div
            ref={setContentRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            data-renderer="user-facing-message"
            class={twMerge(
              "text-sm text-primary-text relative",
              operation()?.activity === "direct_response_v1" &&
                "text-base bg-panel pl-5 pr-5 -ml-2 pt-3 pb-3 rounded-xl border border-primary-border",
              operation()?.activity === "stream_response_v1" &&
                "text-base bg-panel pl-5 pr-5 -ml-2 pt-3 pb-3 rounded-xl border border-primary-border",
            )}
          >
          {/* Hover-based copy button - shows only on message hover when complete */}
          <Show when={props.block.isComplete}>
            <AnimateStaggeredChildren
              show={hovered()}
              enter={{
                opacity: [0, 1],
                y: [-5, 0],
              }}
              stagger={40}
              exit={{
                opacity: [1, 0],
                y: [0, -5],
              }}
            >
              {(animProps) => (
                <div class="sticky top-2 h-0 w-full flex items-center justify-end gap-2 text-xs">
                  <StTooltip
                    placement="top"
                    contentProps={{
                      class: "z-60",
                    }}
                    content={<div>{copied() ? "Copied!" : "Copy answer to clipboard"}</div>}
                  >
                    <StTooltipTrigger
                      ref={animProps.ref}
                      type="button"
                      class="group/btn shadow-md cursor-pointer mt-6 p-1 rounded-lg bg-tile hover:bg-tile-hover border border-muted-border"
                      onClick={handleCopy}
                    >
                      <Dynamic
                        component={copied() ? TbCheck : TbCopy}
                        size={16}
                        class="group-hover/btn:scale-hover group-active/btn:scale-active transition-transform stroke-2"
                      />
                    </StTooltipTrigger>
                  </StTooltip>
                </div>
              )}
            </AnimateStaggeredChildren>
          </Show>

          <Show when={operation()?.activity === "direct_response_v1" || operation()?.activity === "stream_response_v1"}>
            <div class="flex items-center mb-3 -ml-1">
              <LogoNoText class="w-5 h-5" />
            </div>
          </Show>
          <div class="text-base max-w-none">
            <Show
              when={props.block.isComplete && props.block.finalContent?.type === "richText" && props.block.finalContent}
              fallback={
                <SolidStreamingMarkdown
                  doc={markdownStreamer.doc}
                  options={{
                    components: {
                      htmlInline: (props) => {
                        const literal = () => props.node.literal;
                        const isCitation = () => literal()?.startsWith("<cite>") && literal()?.endsWith("</cite>");
                        const citationValue = () => {
                          const lit = literal();
                          if (!lit) return "";
                          return lit.replace("<cite>", "").replace("</cite>", "");
                        };

                        return (
                          <Show when={isCitation()} fallback={null}>
                            <UnifiedCitationRenderer
                              node={{ attrs: { value: citationValue() } }}
                              selected={false}
                              webSearchResults={webSearchResults()}
                            />
                          </Show>
                        );
                      },
                    },
                    classes: {
                      document: "wrap-break-word whitespace-break-spaces",
                      heading: (node) => {
                        switch (node.level) {
                          case 1:
                            return "font-medium text-2xl mb-3 mt-4";
                          case 2:
                            return "font-medium text-xl mb-3 mt-4";
                          case 3:
                            return "font-medium text-lg mb-3 mt-4";
                          case 4:
                            return "font-medium text-base mb-3 mt-4";
                          case 5:
                            return "font-medium text-base mb-3 mt-4";
                          case 6:
                            return "font-medium text-sm mb-3 mt-4";
                          default:
                            return "";
                        }
                      },
                      // paragraph: "mb-2",
                      list: (node) => {
                        switch (node.listData?.type) {
                          case "ordered":
                            return "list-decimal pl-4";
                          case "bullet":
                            return "list-disc pl-4";
                          default:
                            return "";
                        }
                      },
                      thematicBreak: "border-gray-300 dark:border-gray-700 my-4",
                      paragraph: "mb-4",
                      item: "mb-2",
                      code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
                      blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
                      tableWrapper: "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border mb-4",
                      codeBlock: "bg-inset px-2 py-2 mb-2 rounded text-sm overflow-x-auto",
                      table: "",
                      tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
                      tableCell: "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
                      tableHead: "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
                      tableBody: "bg-tile",
                      link: "text-brand underline",
                    },
                  }}
                />
              }
            >
              {(finalContent) => (
                <RichTextEditor
                  doc={finalContent().richText}
                  citationRenderer={(citationProps) => (
                    <UnifiedCitationRenderer
                      node={citationProps.node}
                      selected={citationProps.selected}
                      webSearchResults={webSearchResults()}
                    />
                  )}
                />
              )}
            </Show>
          </div>
          <Show when={!props.block.isComplete}>
            <div class="inline-block w-2 h-4 bg-blue-500 animate-pulse ml-1" />
          </Show>
        </div>

        {/* Text selection handler */}
        <TextSelectionHandler ref={contentRef} isReadOnly={false} />
      </>
      </Show>
    </Show>
  );
};
